import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../../components/feature/PageLayout';
import ArticleCard from '../../components/feature/ArticleCard';
import { categories } from '../../mocks/site';
import { articles } from '../../mocks/articles';
import { useDocMeta } from '../../hooks/useDocMeta';

export default function ColumnPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cat = categories.find((c) => c.slug === slug) || categories[0];
  const list = articles.filter((a) => a.category === cat.name);
  const [tag, setTag] = useState<string>('全部');

  const allTags = useMemo(() => {
    const s = new Set<string>();
    list.forEach((a) => a.tags.forEach((t) => s.add(t)));
    return ['全部', ...Array.from(s)];
  }, [list]);

  const filtered = tag === '全部' ? list : list.filter((a) => a.tags.includes(tag));

  const setMeta = useDocMeta(
    `${cat.name}｜${cat.desc}｜故纸瓜档`,
    `${cat.name}栏目收录${cat.count}篇陈年旧瓜与历史爆料，从旧报道、旧访谈与老照片出发考古重读，还原当年真相。`,
  );
  useEffect(() => {
    setMeta();
    setTag('全部');
    window.scrollTo(0, 0);
  }, [slug, setMeta]);

  const catToAccent: Record<string, string> = {
    陈年旧瓜: 'bg-primary-500',
    考古重读: 'bg-accent-500',
    历史爆料: 'bg-secondary-600',
    旧事重提: 'bg-foreground-800',
    当年真相: 'bg-accent-600',
  };
  const catToIcon: Record<string, string> = {
    陈年旧瓜: 'ri-ancient-pavilion-line',
    考古重读: 'ri-archive-drawer-line',
    历史爆料: 'ri-newspaper-line',
    旧事重提: 'ri-refresh-line',
    当年真相: 'ri-question-line',
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://cshxgc.cn/' },
      { '@type': 'ListItem', position: 2, name: cat.name, item: `https://cshxgc.cn/column/${cat.slug}` },
    ],
  };
  const collection = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${cat.name}｜故纸瓜档`,
    description: cat.desc,
    url: `https://cshxgc.cn/column/${cat.slug}`,
  };

  return (
    <PageLayout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collection) }} />

      {/* 栏目头图 */}
      <section className="relative flex min-h-[300px] w-full items-center overflow-hidden md:min-h-[360px]">
        <img
          src="https://readdy.ai/api/search-image?query=deep%20aged%20archive%20room%20corridor%20with%20dark%20wooden%20shelving%20and%20warm%20amber%20reading%20lamps%2C%20dusty%20old%20files%20and%20newspapers%2C%20moody%20cinematic%20lighting%2C%20archival%20documentary%20photography%2C%20deep%20maroon%20and%20brown%20tones%2C%20no%20text&width=1920&height=800&seq=gossip-column-bg&orientation=landscape"
          alt={`${cat.name}栏目背景`}
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/60 to-foreground-950/40"></div>
        <div className="container-x relative z-10 py-16 pt-28 md:py-20">
          <nav className="flex items-center gap-2 text-sm text-background-50/70" aria-label="面包屑">
            <button onClick={() => navigate('/')} className="cursor-pointer hover:text-accent-300">首页</button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">{cat.name}</span>
          </nav>
          <div className="mt-5 flex items-center gap-4">
            <span className={`flex h-14 w-14 items-center justify-center rounded-md text-2xl text-background-50 ${catToAccent[cat.name] || 'bg-primary-500'}`}>
              <i className={catToIcon[cat.name] || 'ri-archive-line'}></i>
            </span>
            <div>
              <h1 className="font-display text-3xl font-black text-background-50 md:text-5xl">{cat.name}</h1>
              <p className="mt-1 text-sm text-background-50/70">共 {cat.count} 篇档案 · 持续考古重读中</p>
            </div>
          </div>
          <p className="mt-5 max-w-2xl text-sm leading-7 text-background-50/85">{cat.desc}</p>
        </div>
      </section>

      {/* 标签筛选 */}
      <section className="bg-background-100">
        <div className="container-x py-10">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 flex items-center gap-1.5 text-sm text-foreground-500">
              <i className="ri-price-tag-3-line"></i>筛选标签
            </span>
            {allTags.map((t) => (
              <button
                key={t}
                onClick={() => setTag(t)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  tag === t
                    ? 'bg-primary-600 text-background-50'
                    : 'border border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between">
            <h2 className="font-display text-xl font-black text-foreground-900">
              {tag === '全部' ? '本栏目全部旧档' : `标签「${tag}」相关旧档`}
              <span className="ml-2 align-middle text-sm font-normal text-foreground-500">{filtered.length} 篇</span>
            </h2>
            <span className="hidden items-center gap-1.5 text-sm text-foreground-500 sm:inline-flex">
              <i className="ri-eye-line"></i>累计 {(list.reduce((s, a) => s + a.views, 0) / 10000).toFixed(1)}w 阅读
            </span>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <i className="ri-inbox-2-line text-4xl text-background-300"></i>
              <p className="mt-3 text-foreground-600">这个标签下暂时没有更多档案</p>
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
}